import type { ConsentApp, ConsentBasis, ConsentCookie } from "@cede/types";
import { API_ENDPOINT } from "@cede/utils";
import { buildConsent, clearVendorCookies, readConsentCookie, writeConsentCookie } from "./cookie";
import { mixpanelClient } from "@/lib/mixpanel";
import { disableSentryReplay, enableSentryReplay } from "@/lib/sentry-replay";
import { useConsentStore } from "@/store/consentStore";
import { apiHelper } from "@/utils/api";

/**
 * Turns a consent decision into the things it actually controls.
 *
 * Everything non-essential is started here and nowhere else, so there is one
 * place to check when asking "what fires without consent".
 */

const APP: ConsentApp = "hub";

function startAnalytics(): void {
  mixpanelClient.start();
  enableSentryReplay();
}

function stopAnalytics(): void {
  disableSentryReplay();
  // Its device and distinct ids live in localStorage, which the cookie sweep
  // below cannot reach; only a reset removes them.
  mixpanelClient.reset();
  // Whatever a visit before the banner shipped left behind, so a refusal is not
  // contradicted by the cookies still sitting in the browser.
  clearVendorCookies();
}

/**
 * Lets analytics run where the banner is never shown, without recording a
 * decision nobody was asked to make.
 */
export function allowAnalyticsWithoutAsking(): void {
  useConsentStore.getState().setDecided(true);
  startAnalytics();
}

/** Applies a decision already on file, on a page load that did not ask. */
export function applyStoredConsent(consent: ConsentCookie): void {
  useConsentStore.getState().setDecided(consent.analytics);
  if (consent.analytics) startAnalytics();
  else stopAnalytics();

  if (!consent.synced) void syncConsent(consent);
}

/**
 * Records a fresh decision: cookie first, then the tags, then the audit row.
 *
 * The receipt is sent last and never awaited. It exists to prove the choice
 * later, so a slow or failed request must not delay honouring it now.
 */
export function recordConsentDecision({ analytics, basis }: { analytics: boolean; basis: ConsentBasis }): void {
  const consent = buildConsent({ analytics, basis });
  writeConsentCookie(consent);
  useConsentStore.getState().setDecided(analytics);

  if (analytics) startAnalytics();
  else stopAnalytics();

  void syncConsent(consent);
}

/**
 * Withdrawal needs a reload: gtag and GTM cannot be unloaded from a live page,
 * so the only honest way to stop them is a fresh document. A grant needs no
 * reload, since the visitor may be mid-flow.
 */
export function revokeConsent(): void {
  recordConsentDecision({ analytics: false, basis: "user" });
  window.location.reload();
}

async function syncConsent(consent: ConsentCookie): Promise<void> {
  try {
    await apiHelper.postOrThrow(API_ENDPOINT.CONSENT_RECORD, {
      bodyParams: {
        consentId: consent.id,
        analytics: consent.analytics,
        policyVersion: consent.pv,
        app: APP,
      },
    });
  } catch {
    // Left unsynced on purpose: the next page load retries it. The endpoint is
    // idempotent on `consentId`, so a retry cannot double-count a decision.
    return;
  }

  // Re-read rather than reuse: the visitor may have changed their mind while
  // this was in flight, and that newer decision must not be overwritten.
  const current = readConsentCookie();
  if (current?.id !== consent.id) return;
  writeConsentCookie({ ...current, synced: true });
}
